import { createContext, useContext, useState, useEffect, useMemo } from "react";
import en from "./strings";
import fa from "./fa";
import ar from "./ar";
import fr from "./fr";
import de from "./de";
import es from "./es";
import ru from "./ru";
import pt from "./pt";
import zh from "./zh";
import tr from "./tr";
import hi from "./hi";
import it from "./it";
import { auth, getUIStrings, setUIStrings } from "../firebase";
import { ACTIVELY_TRANSLATED_LANGS } from "../constants";
import { formatNumber } from "../utils";

// ── Static bundles ──────────────────────────────────────────────
const STATIC = { English: en, Farsi: fa, Arabic: ar, French: fr, German: de, Spanish: es, Russian: ru, Portuguese: pt, Chinese: zh, Turkish: tr, Hindi: hi, Italian: it };

// ── RTL languages ───────────────────────────────────────────────
export const RTL_LANGS = new Set(["Farsi", "Arabic", "Hebrew", "Urdu", "Pashto", "Kurdish", "Azerbaijani"]);

// ── Deep merge: fills any missing keys from English fallback ─────
function merge(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override || {})) {
    if (Array.isArray(override[key]) && override[key].length > 0) {
      result[key] = override[key];
    } else if (override[key] && typeof override[key] === "object" && !Array.isArray(override[key])) {
      result[key] = merge(base[key] || {}, override[key]);
    } else if (override[key] !== undefined && override[key] !== null && override[key] !== "") {
      result[key] = override[key];
    }
  }
  return result;
}

// ── Flatten nested object to { "a.b.c": value } for API call ────
function flatten(obj, prefix = "") {
  return Object.entries(obj).reduce((acc, [k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    if (Array.isArray(v)) {
      v.forEach((item, i) => { acc[`${key}.${i}`] = item; });
    } else if (v && typeof v === "object") {
      Object.assign(acc, flatten(v, key));
    } else {
      acc[key] = v;
    }
    return acc;
  }, {});
}

// ── Reconstruct nested object from flat keys ─────────────────────
function unflatten(flat) {
  const result = {};
  for (const [dotKey, val] of Object.entries(flat)) {
    const parts = dotKey.split(".");
    let cur = result;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) {
        cur[p] = val;
      } else {
        if (!cur[p]) cur[p] = {};
        cur = cur[p];
      }
    });
  }
  // Convert numeric-keyed objects back to arrays
  function toArrays(node) {
    if (!node || typeof node !== "object") return node;
    const keys = Object.keys(node);
    if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
      const arr = [];
      keys.sort((a, b) => +a - +b).forEach(k => { arr[+k] = toArrays(node[k]); });
      return arr;
    }
    return Object.fromEntries(keys.map(k => [k, toArrays(node[k])]));
  }
  return toArrays(result);
}

// ── Generate UI strings for unknown language via Haiku ───────────
async function generateUIStrings(lang) {
  const proxyUrl = process.env.REACT_APP_PROXY_URL || "http://localhost:8080";
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : "";
  const flat = flatten(en);
  const payload = JSON.stringify(flat, null, 0);
  const sys = `You are a professional translator. Translate every value in the provided JSON object into ${lang}. Rules:
- Keep all {placeholder} tokens exactly as-is (e.g. {name}, {n}, {delta})
- Keep emoji characters as-is
- Keep technical strings unchanged: "claude-sonnet-5 · $3/MTok in · $15/MTok out", and all ISO dates
- Return ONLY valid JSON with the same keys, no extra text or markdown`;
  const res = await fetch(`${proxyUrl}/api/claude`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({
      model: "claude-sonnet-5",
      max_tokens: 16000,
      system: sys,
      messages: [{ role: "user", content: `Translate to ${lang}:\n${payload}` }],
    }),
  });
  if (!res.ok) throw new Error("Translation API " + res.status);
  const d = await res.json();
  const text = d.content.filter(b => b.type === "text").map(b => b.text).join("");
  const flatTranslated = JSON.parse(text.replace(/```json|```/g, "").trim());
  return unflatten(flatTranslated);
}

// ── Context & Provider ───────────────────────────────────────────
const I18nContext = createContext({ t: (k) => k, isRTL: false });

export function I18nProvider({ baseLanguage, children }) {
  const lang = baseLanguage || "English";
  const staticBundle = STATIC[lang];
  // Only genuinely dynamic (non-static) languages need async resolution — this
  // state holds the fetched/generated strings for the *current* `lang` only.
  const [dynamicStrings, setDynamicStrings] = useState(null);

  useEffect(() => {
    if (staticBundle) return;  // resolved synchronously below, no fetch needed
    setDynamicStrings(null);  // clear any previous language's cached strings while this one loads
    let cancelled = false;
    // Dynamic: check Firestore cache, then call Haiku
    getUIStrings(lang)
      .catch(() => null)  // Firestore failure (e.g. rules not deployed) → treat as cache miss
      .then(cached => {
        if (cancelled) return;
        if (cached) {
          setDynamicStrings(merge(en, cached));
          return;
        }
        // Generation guard: a cache miss for a language outside the actively-translated set
        // (e.g. a legacy profile with a frozen/cut baseLanguage, or a custom EXTRA_LANGS pick)
        // skips the AI call entirely rather than spending on a language we no longer generate
        // for. Never blocks the cache read above — only new generation.
        if (!ACTIVELY_TRANSLATED_LANGS.includes(lang)) {
          console.warn(`Translation generation skipped: '${lang}' is a frozen/legacy language, not actively translated`);
          return;
        }
        return generateUIStrings(lang)
          .then(generated => {
            setUIStrings(lang, generated).catch(() => {});
            if (!cancelled) setDynamicStrings(merge(en, generated));
          });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang, staticBundle]);

  // Static bundles (the vast majority of supported base languages) resolve
  // synchronously during render via useMemo, in lockstep with `isRTL` below —
  // both derive straight from `lang` in the same render pass, so `dir` and
  // the actual translated text never disagree about which language is
  // current. Previously `strings` was seeded once and only ever updated by
  // the effect above (even for static bundles), which lagged one render
  // behind `lang`/`isRTL` — enough for a consumer that reads `t()` in its own
  // mount effect (e.g. Babi's first chat message right after onboarding) to
  // capture the *previous* language's strings while `dir` already reflected
  // the new one. Only real dynamic (non-static) languages still wait on the
  // effect above.
  const strings = useMemo(
    () => staticBundle ? merge(en, staticBundle) : (dynamicStrings || en),
    [staticBundle, dynamicStrings]
  );

  const isRTL = RTL_LANGS.has(lang);

  // t(path, params) — "dashboard.greeting" + { name: "Ali" } → "Hello, Ali! 👋"
  const t = (path, params = {}) => {
    const keys = path.split(".");
    let val = strings;
    for (const k of keys) { val = val?.[k]; }
    if (val === undefined || val === null) {
      // fallback to English
      let fb = en;
      for (const k of keys) { fb = fb?.[k]; }
      val = fb;
    }
    if (Array.isArray(val)) return val;
    if (typeof val !== "string") return path;
    return Object.entries(params).reduce(
      (s, [k, v]) => s.replace(
        new RegExp(`\\{${k}\\}`, "g"),
        typeof v === "number" ? formatNumber(v, lang) : String(v ?? "")
      ),
      val
    );
  };

  // Resolves a canonical language name (e.g. "Turkish", stored/compared in English
  // regardless of UI language) to how the CURRENT UI language names it — e.g. "ترکی"
  // when strings is the Farsi bundle. Falls back to the raw name for anything outside
  // goals.langNames (custom "Other language…" picks) so it never renders a dotted key path.
  const langName = (name) => strings?.goals?.langNames?.[name] || name;

  return <I18nContext.Provider value={{ t, isRTL, lang, strings, langName }}>{children}</I18nContext.Provider>;
}

export const useT = () => useContext(I18nContext);
